import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, CameraOff, CheckCircle2, XCircle, Loader2, Keyboard, Clock } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { euro } from "@/lib/format";
import { toast } from "sonner";

export default function Validate() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {ok, ...} or {error}
  const [scanning, setScanning] = useState(false);
  const [amount, setAmount] = useState("");
  const [manualDisc, setManualDisc] = useState("");
  const [qty, setQty] = useState("1");
  const [unitPrice, setUnitPrice] = useState("");
  const [amtBusy, setAmtBusy] = useState(false);
  const [amtDone, setAmtDone] = useState(false);
  const [voided, setVoided] = useState(false);
  const [outOfWindow, setOutOfWindow] = useState(null);
  const [pendingCode, setPendingCode] = useState("");
  const scannerRef = useRef(null);
  const regionId = "kn-qr-region";

  const isManualDisc = result?.ok && (result.discount_type === "price" || result.discount_type === "bogo");
  const isItem = result?.ok && result.scope === "item";
  const belowMin = result?.ok && (result.min_spend || 0) > 0 && amount !== "" && (parseFloat(amount) || 0) < result.min_spend;
  const computeDiscountEur = () => {
    if (!result?.ok) return 0;
    const amt = parseFloat(amount) || 0;
    if (result.discount_type === "percent") return Math.round(amt * (result.discount_value || 0)) / 100;
    if (result.discount_type === "fixed") return result.discount_value || 0;
    return parseFloat(manualDisc) || 0; // price / bogo -> manual
  };

  const itemSubtotal = () => (parseInt(qty) || 0) * (parseFloat(unitPrice) || 0);
  const itemDiscountEur = () => {
    if (!result?.ok) return 0;
    const sub = itemSubtotal();
    if (result.discount_type === "percent") return Math.round(sub * (result.discount_value || 0)) / 100;
    // fixed: sconto € per ogni articolo × quantità
    return Math.round((result.discount_value || 0) * (parseInt(qty) || 0) * 100) / 100;
  };

  const submitItemAmount = async () => {
    if (!(parseInt(qty) > 0) || !(parseFloat(unitPrice) > 0)) return;
    setAmtBusy(true);
    try {
      await api.post(`/vendor/claims/${result.claim_id}/amount`,
        { amount_spent: Math.round(itemSubtotal() * 100) / 100, discount_eur: Math.round(itemDiscountEur() * 100) / 100 },
        { headers: authHeaders("vendor") });
      setAmtDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error((typeof detail === "string" ? detail : null) || "Errore nell'invio della validazione");
    } finally { setAmtBusy(false); }
  };

  const submitAmount = async () => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt < 0) return;
    setAmtBusy(true);
    try {
      await api.post(`/vendor/claims/${result.claim_id}/amount`,
        { amount_spent: Math.round(amt * 100) / 100, discount_eur: Math.round(computeDiscountEur() * 100) / 100 },
        { headers: authHeaders("vendor") });
      setAmtDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "min_spend_not_met") {
        setResult({ ok: false, error: detail.message });
        setAmount(""); setManualDisc("");
      } else {
        toast.error((typeof detail === "string" ? detail : null) || "Errore nell'invio dell'importo");
      }
    } finally { setAmtBusy(false); }
  };

  const doRedeem = async (raw, force = false) => {
    const val = (raw || "").trim();
    if (!val) return;
    setBusy(true);
    setResult(null);
    setAmount(""); setManualDisc(""); setAmtDone(false); setVoided(false);
    setQty("1"); setUnitPrice("");
    try {
      const r = await api.post("/vendor/redeem", { code: val, force }, { headers: authHeaders("vendor") });
      setResult({ ok: true, ...r.data });
      setCode(""); setOutOfWindow(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "out_of_window") {
        setPendingCode(val);
        setOutOfWindow(detail);
      } else {
        setResult({ ok: false, error: (typeof detail === "string" ? detail : null) || "Codice non valido" });
      }
    } finally {
      setBusy(false);
    }
  };

  const resetAll = () => {
    setResult(null); setAmount(""); setManualDisc(""); setAmtDone(false); setVoided(false); setCode("");
    setQty("1"); setUnitPrice("");
  };

  const voidMin = async () => {
    setAmtBusy(true);
    try {
      await api.post(`/vendor/claims/${result.claim_id}/void-min`,
        { amount_spent: Math.round((parseFloat(amount) || 0) * 100) / 100 },
        { headers: authHeaders("vendor") });
      setVoided(true); setAmtDone(true);
      toast.success("Coupon annullato: spesa minima non raggiunta");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setAmtBusy(false); }
  };

  const stopScan = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch (_) {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScan = async () => {
    setResult(null);
    setScanning(true);
    // NOTE: iOS Safari only grants the camera if getUserMedia is reached inside the
    // same user-gesture task. A setTimeout here breaks that chain -> permission denied
    // even when already granted. So we start the scanner directly (no setTimeout).
    const config = {
      fps: 15,
      qrbox: (w, h) => { const m = Math.floor(Math.min(w, h) * 0.75); return { width: m, height: m }; },
      // Native BarcodeDetector is far more reliable than the JS fallback (Android). iOS ignores it.
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      rememberLastUsedCamera: true,
    };
    let handled = false;
    const onScan = async (decoded) => { if (handled) return; handled = true; await stopScan(); doRedeem(decoded); };
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || "");
    try {
      const scanner = new Html5Qrcode(regionId, { verbose: false });
      scannerRef.current = scanner;
      try {
        // iOS: keep constraints minimal (facingMode only) to avoid OverconstrainedError.
        const envConstraints = isIOS
          ? { facingMode: "environment" }
          : { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } };
        await scanner.start(envConstraints, config, onScan, () => {});
      } catch (e1) {
        // Fallback: pick a camera by id (prefer the back one).
        const cams = await Html5Qrcode.getCameras();
        if (!cams || !cams.length) throw e1;
        const back = cams.find((c) => /back|rear|environment|posteriore/i.test(c.label || "")) || cams[cams.length - 1];
        await scanner.start(back.id, config, onScan, () => {});
      }
    } catch (err) {
      setScanning(false);
      scannerRef.current = null;
      const standalone = window.navigator.standalone === true;
      let msg = "Impossibile accedere alla fotocamera. Consenti l'accesso alla fotocamera nelle impostazioni del browser, oppure usa il codice manuale.";
      if (isIOS && standalone) {
        msg = "Su iPhone la fotocamera non è disponibile dall'app aggiunta alla schermata Home. Apri KoupoNow in Safari per scansionare, oppure inserisci le 6 cifre manualmente.";
      } else if (isIOS) {
        msg = "Su iPhone consenti la fotocamera da Impostazioni ▸ Safari ▸ Fotocamera (o tocca «aA» nella barra indirizzi ▸ Impostazioni sito). In alternativa inserisci le 6 cifre manualmente.";
      }
      setResult({ ok: false, error: msg });
    }
  };

  useEffect(() => () => { stopScan(); }, []);

  const computedDiscount = () => {
    if (!result?.ok) return null;
    const { discount_type, discount_value, min_spend } = result;
    if (result.scope === "item") {
      const base = discount_type === "fixed" ? `Sconto ${euro(discount_value)} a pezzo` : `Sconto ${discount_value}%`;
      return `${base}${result.item_name ? ` su ${result.item_name}` : ""}`;
    }
    if (discount_type === "percent" && min_spend > 0) {
      return `Sconto ${discount_value}% su spesa min. ${euro(min_spend)} = ${euro((min_spend * discount_value) / 100)}`;
    }
    if (discount_type === "percent") return `Sconto ${discount_value}%`;
    if (discount_type === "fixed") return `Sconto ${euro(discount_value)}`;
    if (discount_type === "price") return `Prezzo speciale ${euro(discount_value)}`;
    if (discount_type === "bogo") return "2x1 / Omaggio";
    return null;
  };

  return (
    <div className="max-w-lg mx-auto space-y-5 kn-fade-up">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Valida in cassa</h1>
        <p className="text-slate-500">Scansiona il QR del cliente o inserisci le 6 cifre.</p>
      </div>

      {/* Result banner */}
      {result && (
        <div data-testid="redeem-result" className={`rounded-2xl p-5 ${result.ok ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"} kn-pop`}>
          {result.ok ? (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-500 shrink-0" />
              <div>
                <p className="font-extrabold text-emerald-800 text-lg">Coupon valido!</p>
                <p className="text-emerald-700">{result.title} · {result.client_name}</p>
                {computedDiscount() && <p className="mt-1 font-bold text-emerald-800">{computedDiscount()}</p>}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <XCircle className="h-8 w-8 text-red-500 shrink-0" />
              <div><p className="font-extrabold text-red-800 text-lg">Non valido</p><p className="text-red-700">{result.error}</p></div>
            </div>
          )}
        </div>
      )}

      {/* Amount entry after a valid redemption */}
      {result?.ok && result.claim_id && (
        amtDone ? (
          <div className="kn-card p-4 flex items-center gap-3" data-testid="amount-sent">
            <CheckCircle2 className={`h-6 w-6 shrink-0 ${voided ? "text-[#DC2626]" : "text-emerald-500"}`} />
            <p className="text-sm text-slate-700 font-semibold">{voided ? "Coupon annullato: spesa minima non raggiunta. È tornato disponibile tra i coupon del cliente, che potrà usarlo quando raggiunge la soglia." : "Importo inviato al cliente per la conferma. Riceverà una notifica."}</p>
          </div>
        ) : isItem ? (
          <div className="kn-card p-4 space-y-3" data-testid="item-amount-form">
            <p className="font-bold text-slate-800">Offerta su un articolo{result.item_name ? `: ${result.item_name}` : ""}</p>
            <p className="text-xs text-slate-500">Inserisci quanti pezzi hai venduto e il prezzo del singolo articolo: calcoliamo in automatico lo sconto previsto dal coupon.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-600">Quantità venduta</label>
                <Input data-testid="item-qty-input" type="number" min="1" step="1" value={qty}
                  onChange={(e) => setQty(e.target.value)} placeholder="Es. 2" className="h-12 rounded-xl text-lg font-bold" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-600">Prezzo articolo (€)</label>
                <Input data-testid="item-price-input" type="number" min="0" step="0.01" value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)} placeholder="Es. 10.00" className="h-12 rounded-xl text-lg font-bold" />
              </div>
            </div>
            {itemSubtotal() > 0 && (
              <div className="rounded-xl bg-emerald-50 p-3 space-y-1" data-testid="item-preview">
                <p className="text-sm text-slate-600">Subtotale: <b>{euro(itemSubtotal())}</b> ({parseInt(qty) || 0} × {euro(parseFloat(unitPrice) || 0)})</p>
                <p className="text-base font-extrabold text-emerald-800">Inserisci in cassa questo sconto: −{euro(itemDiscountEur())}</p>
                <p className="text-sm font-bold text-emerald-800">Totale da incassare: {euro(Math.max(0, itemSubtotal() - itemDiscountEur()))}</p>
              </div>
            )}
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5" data-testid="item-cashier-hint">
              Applica questo sconto in cassa, poi invia la validazione per la conferma del cliente.
            </p>
            <Button onClick={submitItemAmount} disabled={amtBusy || !(parseInt(qty) > 0) || !(parseFloat(unitPrice) > 0)} data-testid="item-amount-submit" className="w-full h-12 rounded-xl font-bold">
              {amtBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Invia la validazione"}
            </Button>
          </div>
        ) : (
          <div className="kn-card p-4 space-y-3" data-testid="amount-form">
            <p className="font-bold text-slate-800">Registra la spesa</p>
            <p className="text-xs text-slate-500">Inserisci quanto ha speso il cliente: calcoliamo lo sconto e chiediamo conferma al cliente.</p>
            {result.min_spend > 0 && (
              <p className="text-xs font-bold text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5" data-testid="min-spend-hint">
                Spesa minima richiesta: {euro(result.min_spend)}
              </p>
            )}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-slate-600">Importo speso (€)</label>
              <Input data-testid="amount-input" type="number" min="0" step="0.01" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder="Es. 50.00" className="h-12 rounded-xl text-lg font-bold" />
            </div>
            {isManualDisc && (
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-600">Sconto applicato (€)</label>
                <Input data-testid="manual-disc-input" type="number" min="0" step="0.01" value={manualDisc}
                  onChange={(e) => setManualDisc(e.target.value)} placeholder="Es. 8.00" className="h-11 rounded-xl" />
              </div>
            )}
            {amount !== "" && belowMin && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-red-700 font-bold text-sm flex items-start gap-2" data-testid="below-min-warning">
                <XCircle className="h-5 w-5 shrink-0" />
                <span>Sotto la spesa minima di {euro(result.min_spend)}: lo sconto non è valido. Chiedi al cliente di raggiungere la soglia.</span>
              </div>
            )}
            {amount !== "" && !belowMin && (
              <div className="rounded-xl bg-emerald-50 p-3 text-emerald-800 font-bold text-sm" data-testid="amount-preview">
                Sconto: {euro(computeDiscountEur())} · Il cliente paga {euro(Math.max(0, (parseFloat(amount) || 0) - computeDiscountEur()))}
              </div>
            )}
            <Button onClick={submitAmount} disabled={amtBusy || amount === "" || belowMin} data-testid="amount-submit" className="w-full h-12 rounded-xl font-bold">
              {amtBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Invia al cliente per conferma"}
            </Button>
            {belowMin && (
              <Button variant="outline" onClick={voidMin} disabled={amtBusy} data-testid="void-min-btn"
                className="w-full h-12 rounded-xl font-bold gap-2 text-[#DC2626] border-[#DC2626]/40 hover:bg-red-50">
                <XCircle className="h-4 w-4" /> Annulla coupon (spesa minima non raggiunta)
              </Button>
            )}
          </div>
        )
      )}

      {/* Once a coupon is validated, hide scan/manual and offer a reset */}
      {result?.ok ? (
        <Button variant="outline" onClick={resetAll} data-testid="validate-another" className="w-full h-12 rounded-xl font-bold gap-2">
          <Camera className="h-4 w-4" /> Valida un altro coupon
        </Button>
      ) : (
        <>
          {/* Camera — region stays mounted so getUserMedia can run inside the tap gesture (iOS). */}
          <div className="kn-card p-4 space-y-3">
            <div id={regionId} className={`rounded-xl overflow-hidden bg-black ${scanning ? "min-h-[240px]" : "h-0"}`} data-testid="qr-region" />
            {scanning ? (
              <>
                <p className="text-xs text-slate-500 text-center">Inquadra il QR del cliente al centro del riquadro, ben illuminato e a fuoco.</p>
                <Button variant="outline" onClick={stopScan} className="w-full h-11 rounded-xl gap-2" data-testid="stop-scan"><CameraOff className="h-4 w-4" /> Ferma scanner</Button>
              </>
            ) : (
              <Button onClick={startScan} className="w-full h-14 rounded-xl font-bold text-base gap-2" data-testid="start-scan">
                <Camera className="h-5 w-5" /> Scansiona QR
              </Button>
            )}
          </div>

          {/* Manual */}
          <div className="kn-card p-4 space-y-3">
            <div className="flex items-center gap-2 text-slate-500 text-sm font-semibold"><Keyboard className="h-4 w-4" /> Inserimento manuale</div>
            <div className="flex gap-2">
              <Input
                data-testid="manual-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doRedeem(code)}
                placeholder="Es. 482913"
                className="h-12 rounded-xl text-center text-xl font-bold tracking-widest"
              />
              <Button onClick={() => doRedeem(code)} disabled={busy} data-testid="validate-btn" className="h-12 px-6 rounded-xl font-bold">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Valida"}
              </Button>
            </div>
            <p className="text-xs text-slate-400">Le 6 cifre cambiano ogni 30 secondi. Chiedi al cliente il codice aggiornato.</p>
          </div>
        </>
      )}

      <Dialog open={!!outOfWindow} onOpenChange={(o) => { if (!o) setOutOfWindow(null); }}>
        <DialogContent className="sm:max-w-sm rounded-3xl" data-testid="out-of-window-dialog">
          <div className="text-center pt-2">
            <div className="h-14 w-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-3">
              <Clock className="h-7 w-7 text-amber-600" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-900">Fuori orario di validità</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{outOfWindow?.message}</p>
            <button
              data-testid="force-validate-btn"
              onClick={() => { const c = pendingCode; setOutOfWindow(null); doRedeem(c, true); }}
              className="mt-4 w-full h-12 rounded-xl bg-amber-600 text-white font-bold"
            >
              Forza validazione
            </button>
            <button onClick={() => setOutOfWindow(null)} className="mt-2 w-full h-11 rounded-xl text-slate-500 font-bold text-sm">Annulla</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}